import { useEffect } from "react";

import type { Action } from "./types";

type KeyBindingMap = Record<string, (event: KeyboardEvent) => void>;

// tinykeys' package.json exports field is missing a "types" condition,
// which breaks moduleResolution: "bundler". We declare the signature
// locally and suppress the import error.
import { tinykeys as _tinykeys } from "tinykeys";

const tinykeys = _tinykeys as (
  target: Window | HTMLElement,
  bindings: KeyBindingMap,
) => () => void;

/** True when the event target is a text-editable element (input, textarea, select, or contentEditable). */
const isEditableTarget = (e: KeyboardEvent): boolean => {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable
  );
};

/** True when the tinykeys binding string includes a modifier key. */
const hasModifier = (key: string): boolean =>
  /\$mod|Control|Alt|Meta|Shift/.test(key);

/**
 * Binds keyboard shortcuts for all registered actions via tinykeys.
 * Re-binds whenever the action list changes; cleanup is automatic.
 */
export const ShortcutBinder = ({ actions }: { actions: Action[] }) => {
  useEffect(() => {
    const bindings: KeyBindingMap = {};

    for (const action of actions) {
      if (!action.shortcut) continue;
      for (const key of action.shortcut) {
        bindings[key] = (e) => {
          if (isEditableTarget(e) && !hasModifier(key)) return;
          if (action.shortcutWhen && !action.shortcutWhen(e)) return;
          e.preventDefault();
          (action.shortcutPerform ?? action.perform)();
        };
      }
    }

    if (Object.keys(bindings).length === 0) return;

    return tinykeys(window, bindings);
  }, [actions]);

  return null;
};
